import { useCallback, useState } from 'react';

import { useAtomValue } from 'jotai';
import { startCase } from 'lodash';

import { Analysis, convertUnit, isFiniteNumber, UserPreferredUnit } from '@core';
import { Swing } from '@data';
import { useUserSettingsWithDefaults } from '@hooks';
import { useTranslate } from '@i18n';
import { FAVORITE_SWINGS_PAGE_SIZE, useActivitySwings, useFavoriteSwings, useSwingAnalysis } from '@queries';
import { activeUserIdAtom, activeUserSettingsAtom, activityIdAtom, layoutKeyParameterNodeAtom } from '@store';

import { EmptySwingCard, SwingCard } from '../SwingCard/SwingCard';
import { CardListSlider, SwingCardGrid, SwingCardGridPagination } from '../SwingCard/SwingCardList';

import * as css from './MySwingsTab.css';

const INITIAL_PAGE = 1;

type SwingCardWithParamProps = Swing & { number: number; onClick: (uuid: string) => void; isSelected: boolean };

const getParam = (userPreferredUnit?: UserPreferredUnit, keyParamId?: string, analysis?: Analysis | null) => {
    if (!keyParamId || !analysis) {
        return;
    }
    const paramValue = keyParamId && analysis?.data.analysis?.parameter_values?.[keyParamId];
    if (!paramValue) {
        return;
    }
    const { value, unit } = paramValue;
    const converted = convertUnit({ unit, value, userPreferredUnit });
    const displayValue = isFiniteNumber(converted.value) ? String(converted.value) : '— ';
    return `${displayValue}${converted.symbol}`;
};

const SwingCardWithParam = ({ onClick, isSelected, number, ...props }: SwingCardWithParamProps) => {
    const parameterNode = useAtomValue(layoutKeyParameterNodeAtom);
    const activeUserId = useAtomValue(activeUserIdAtom);
    const parameterId = parameterNode?.id.split('.')[1];
    const t = useTranslate();
    const parameterName = parameterNode?.i18n ? t(parameterNode.i18n) : startCase(parameterId);
    const { data: userSettings } = useUserSettingsWithDefaults(activeUserId);

    const { data: analysis } = useSwingAnalysis(props.uuid);

    return (
        <SwingCard
            key={props.uuid}
            {...props}
            number={number}
            title={
                getParam(
                    userSettings?.userPreferredUnit,
                    parameterId,
                    analysis?.fullAnalysis || analysis?.quickAnalysis,
                ) ||
                props.name ||
                `Swing ${number}`
            }
            isSelected={isSelected}
            onClick={onClick}
            info={parameterName}
        />
    );
};

type MySwingsTabProps = {
    filter: string;
    setComparisonSwing: (uuid: string) => void;
    activeUserId?: string;
};

export const MySwingsTab = ({ filter, setComparisonSwing, activeUserId }: MySwingsTabProps) => {
    const activityId = useAtomValue(activityIdAtom);
    const userSettings = useAtomValue(activeUserSettingsAtom);
    const { data: swings = [] } = useActivitySwings(activityId);

    const [currentPage, setCurrentPage] = useState(INITIAL_PAGE);
    const { data: favoriteSwings = [], count } = useFavoriteSwings(currentPage - 1, filter, activeUserId);
    const totalPages = Math.ceil(count / FAVORITE_SWINGS_PAGE_SIZE) || 1;

    const nextPage = useCallback(() => {
        if (currentPage === totalPages) return;
        setCurrentPage(currentPage + 1);
    }, [currentPage, totalPages]);
    const prevPage = useCallback(() => {
        if (currentPage === 1) return;
        setCurrentPage(currentPage - 1);
    }, [currentPage]);

    return (
        <>
            <CardListSlider title="Current Session" emptyListNode={<EmptySwingCard />}>
                {swings.map((swing) => (
                    <SwingCardWithParam
                        key={swing.uuid}
                        {...swing}
                        number={swing.number}
                        isSelected={swing.uuid === userSettings.comparisonSwingId}
                        onClick={setComparisonSwing}
                    />
                ))}
            </CardListSlider>
            <div className={css.root} key="favorites">
                <SwingCardGrid
                    rowCount={2}
                    fullHeight
                    title="Favorite Swings"
                    emptyListNode={<EmptySwingCard />}
                    pagination={
                        <SwingCardGridPagination
                            nextPage={nextPage}
                            prevPage={prevPage}
                            currentPage={currentPage}
                            totalPages={totalPages}
                        />
                    }
                >
                    {favoriteSwings.map((swing, index) => (
                        <SwingCard
                            key={swing.uuid + index}
                            {...swing}
                            title={swing.name || 'Untitled swing'}
                            isSelected={swing.uuid === userSettings.comparisonSwingId}
                            onClick={setComparisonSwing}
                        />
                    ))}
                </SwingCardGrid>
            </div>
        </>
    );
};
